import { OmniElement, OmniStyleElement, html, css } from 'omni-ui';
import { pixToRem } from '../../helpers/utils.js';

OmniStyleElement.register();

/**
 * Class for Attention Calculator Score Module Element
 * @extends OmniElement
 */
export class CampaignAttentionCalculatorScore extends OmniElement {
  /**
   * Attention Calculator Legend Lit Properties
   * @property {Object} schema - Values passed in from wrapper/router
   * @property {Array} completedAttentionSteps - array of completed steps with each element representing the step number
   * @property {Number} score - Running total score for all questions
   * @property {Number} scoreTime - Final time associated with a given score in the schema
   * @property {Number} scoreColor - Color associated with a given score in the schema
   */
  static get properties() {
    return {
      schema: { type: Object },
      completedAttentionSteps: { type: Array },
      score: { type: Number },
      scoreTime: { type: Number },
      scoreColor: { type: String },
    };
  }

  /**
   * Attention Calculator Score module styles
   */
  static get styles() {
    return [
      super.styles,
      css`
        .score-container {
          display: flex;
          justify-content: flex-end;
          align-items: flex-start;
          margin-top: 2rem;
        }
        .score-divider {
          width: 1px;
          height: 5rem;
          align-self: center;
          background-color: rgb(206, 206, 206);
          margin-right: 2rem;
        }
        .attention-level {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          margin-right: 2rem;
        }
        h1.attention-level-value {
          font-size: ${pixToRem(36)};
        }
        .score-value {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
        }
        .score-header {
          display: flex;
        }
        .score-color-dot {
          display: flex;
          border-radius: 50%;
          height: ${pixToRem(50)};
          width: ${pixToRem(50)};
          align-items: center;
          justify-content: center;
        }
        .score-color-dot-text {
          font-size: ${pixToRem(18)};
          color: rgb(var(--rgb-core-light));
        }
      `,
    ];
  }

  /**
   * @description Calculates what range the final score falls within and sets {@link #scoreColor} and {@link #scoreTime} accordingly
   * If both isLower and isHigher are true, the score falls within the corresponding schema range
   */
  _findFinalColor = () => {
    const scoreLegend = this.schema?.score_legend;

    const { color, time_value: timeValue } = Object.values(scoreLegend).find(
      ({ low_end: lowEnd, high_end: highEnd }) => {
        let isLower;
        let isHigher;

        // check if lowEnd is greater than or equal to zero and not null
        if (lowEnd >= 0 && lowEnd !== null) {
          isLower = lowEnd <= this.score;
        }

        // check if highEnd is greater than or equal to zero and not null
        if (highEnd >= 0 && highEnd !== null) {
          isHigher = highEnd === '' ? true : highEnd > this.score;
        }

        return isLower && isHigher;
      },
    );

    this.scoreColor = color;
    this.scoreTime = timeValue;
  };

  /**
   * @returns {html} Rendered HTML for the component
   */
  render() {
    this._findFinalColor();
    return html` <omni-style>
      <div class="score-container">
        <div class="attention-level">
          <h2 class="mb-2">${this.schema.final_recommendation_text}:</h2>
          <h1 class="attention-level-value">
            ${this.completedAttentionSteps.length === this.schema?.steps.length
              ? `${this.scoreTime}s`
              : '-'}
          </h1>
        </div>
      </div>
    </omni-style>`;
  }
}

OmniElement.register(
  'campaign-attention-calculator-score',
  CampaignAttentionCalculatorScore,
);
